import { z } from "zod";

export const EditorOpsZod = z.object({
  originalWidth: z.number(),
  originalHeight: z.number(),
  niveles: z
    .object({
      black: z.number(),
      mid: z.number(),
      white: z.number(),
    })
    .optional(),
  exposicion: z.number().optional(),
  saturacion: z.number().optional(),
  recorte: z
    .object({
      x: z.number(),
      y: z.number(),
      width: z.number(),
      height: z.number(),
    })
    .optional(),
  clonar: z.array(
    z.object({
      from: z.object({
        x: z.number(),
        y: z.number(),
      }),
      to: z.object({
        x: z.number(),
        y: z.number(),
      }),
      size: z.number(),
    })
  ),
});

export type EditorOps = z.infer<typeof EditorOpsZod>;

// Niveles
export const applyLevels = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  black: number,
  mid: number,
  white: number
) => {
  const imgData = ctx.getImageData(0, 0, width, height);
  const d = imgData.data;

  for (let i = 0; i < d.length; i += 4) {
    for (let j = 0; j < 3; j++) {
      let value = d[i + j];
      value = ((value - black) / (white - black)) * 255;
      value = value * (mid / 128);
      d[i + j] = Math.max(0, Math.min(255, value));
    }
  }
  ctx.putImageData(imgData, 0, 0);
};

// Exposición
export const applyExposure = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  value: number
) => {
  const imgData = ctx.getImageData(0, 0, width, height);
  const d = imgData.data;
  const factor = Math.pow(2, value / 100);

  for (let i = 0; i < d.length; i += 4) {
    for (let j = 0; j < 3; j++) {
      let v = d[i + j] * factor;
      d[i + j] = Math.max(0, Math.min(255, v));
    }
  }
  ctx.putImageData(imgData, 0, 0);
};

// Saturación
export const applySaturacion = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  value: number
) => {
  const imgData = ctx.getImageData(0, 0, width, height);
  const d = imgData.data;

  // factor: 0 = gris, 1 = sin cambio, >1 = más saturado
  const factor = 1 + value / 100;

  for (let i = 0; i < d.length; i += 4) {
    const r = d[i] / 255;
    const g = d[i + 1] / 255;
    const b = d[i + 2] / 255;

    // Luminosidad perceptual (Y) como referencia para desaturar
    const gray = 0.2989 * r + 0.587 * g + 0.114 * b;

    // Interpolación entre gris (0) y color original (1)
    const nr = gray + (r - gray) * factor;
    const ng = gray + (g - gray) * factor;
    const nb = gray + (b - gray) * factor;

    d[i] = Math.max(0, Math.min(255, nr * 255));
    d[i + 1] = Math.max(0, Math.min(255, ng * 255));
    d[i + 2] = Math.max(0, Math.min(255, nb * 255));
  }

  ctx.putImageData(imgData, 0, 0);
};

// Recorte
export const applyCrop = (
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; width: number; height: number }
) => {
  const imgData = ctx.getImageData(rect.x, rect.y, rect.width, rect.height);
  ctx.canvas.width = rect.width;
  ctx.canvas.height = rect.height;
  ctx.putImageData(imgData, 0, 0);
};

// Clonar con círculo centrado y previsualización
export const applyClone = (
  ctx: CanvasRenderingContext2D,
  from: { x: number; y: number },
  to: { x: number; y: number },
  size: number
) => {
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = size;
  tempCanvas.height = size;
  const tempCtx = tempCanvas.getContext("2d")!;

  // Copiar bloque centrado en "from"
  tempCtx.drawImage(
    ctx.canvas,
    from.x - size / 2,
    from.y - size / 2,
    size,
    size,
    0,
    0,
    size,
    size
  );

  // Máscara circular
  tempCtx.globalCompositeOperation = "destination-in";
  tempCtx.beginPath();
  tempCtx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
  tempCtx.fill();

  // Pegar centrado en "to"
  ctx.drawImage(tempCanvas, to.x - size / 2, to.y - size / 2);
};

// Aplicar todo el historial sobre el canvas
export const applyOps = (ctx: CanvasRenderingContext2D, ops: EditorOps) => {
  const canvas = ctx.canvas;
  const scaleX = canvas.width / ops.originalWidth;
  const scaleY = canvas.height / ops.originalHeight;

  // Draw original image
  ctx.drawImage(ctx.canvas, 0, 0, canvas.width, canvas.height);

  // Levels
  if (ops.niveles) {
    applyLevels(
      ctx,
      canvas.width,
      canvas.height,
      ops.niveles.black,
      ops.niveles.mid,
      ops.niveles.white
    );
  }

  // Exposure
  if (ops.exposicion !== undefined) {
    applyExposure(ctx, canvas.width, canvas.height, ops.exposicion);
  }

  if (ops.saturacion !== undefined) {
    applySaturacion(ctx, canvas.width, canvas.height, ops.saturacion);
  }

  // Clonaciones
  ops.clonar.forEach((op) => {
    const scaledSize = op.size * ((scaleX + scaleY) / 2);
    applyClone(
      ctx,
      { x: op.from.x * scaleX, y: op.from.y * scaleY },
      { x: op.to.x * scaleX, y: op.to.y * scaleY },
      scaledSize
    );
  });

  // Crop last
  if (ops.recorte) {
    const r = {
      x: ops.recorte.x * scaleX,
      y: ops.recorte.y * scaleY,
      width: ops.recorte.width * scaleX,
      height: ops.recorte.height * scaleY,
    };
    applyCrop(ctx, r);
  }
};

export const loadImageToCanvas = async (
  url: string,
  canvas: HTMLCanvasElement
) => {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.src = url;
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, 0, 0);
      resolve(img);
    };
    img.onerror = reject;
  });
};

export const applyEdits = (
  canvas: HTMLCanvasElement,
  img: HTMLImageElement,
  ops: EditorOps
) => {
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  applyOps(ctx, ops);
};
